import { useMutation, useQuery, useQueryClient } from "react-query";
import { clearStoredAuth } from "../utils";
import { useNavigate } from "react-router-dom";

import { API_URL } from "../config";

type FlightDetailsQuery = {
  flightId: string;
  operatorId?: number | null;
  gufi?: string | null;
  ppsId?: number | null;
  departureAirport?: string | null;
  arrivalAirport?: string | null;
  scheduledDeparture?: string | null;
  aircraftRegistration?: string | null;
  recipientQuery?: string;
  recipientLimit?: number;
  limit?: number;
};

type OverridePayload = {
  operatorId?: number | null;
  flightId?: string | null;
  gufi?: string | null;
  ppsId?: number | null;
  filename?: string | null;
  filePath?: string | null;
  documentType?: string | null;
  direction?: string | null;
  airport?: string | null;
  departureAirport?: string | null;
  arrivalAirport?: string | null;
  companyCode?: string | null;
  flightNumber?: string | null;
  recipients: string[];
  sendAction: EmailDistributionSendAction;
  deliveryKind?: EmailDistributionDeliveryKind | null;
  logicalTargetKey?: string | null;
  summaryFilename?: string | null;
  summaryFilePath?: string | null;
  reason?: string | null;
  attachments?: Array<{
    filename: string;
    filePath?: string | null;
  }>;
  emailSubject?: string | null;
  emailBody?: string | null;
};

const fetchOpsEmailDistributionDetails = async ({
  flightId,
  operatorId,
  gufi,
  ppsId,
  departureAirport,
  arrivalAirport,
  scheduledDeparture,
  aircraftRegistration,
  recipientQuery,
  recipientLimit = 12,
  limit = 500,
}: FlightDetailsQuery): Promise<OpsEmailDistributionFlightDetailsResponse> => {
  const params = new URLSearchParams();
  if (Number.isFinite(operatorId || NaN) && Number(operatorId) > 0) {
    params.set("operatorId", `${operatorId}`);
  }
  if (gufi) {
    params.set("gufi", gufi);
  }
  if (Number.isFinite(ppsId || NaN) && Number(ppsId) > 0) {
    params.set("ppsId", `${ppsId}`);
  }
  if (departureAirport) {
    params.set("departureAirport", departureAirport);
  }
  if (arrivalAirport) {
    params.set("arrivalAirport", arrivalAirport);
  }
  if (scheduledDeparture) {
    params.set("scheduledDeparture", scheduledDeparture);
  }
  if (aircraftRegistration) {
    params.set("aircraftRegistration", aircraftRegistration);
  }
  if (recipientQuery) {
    params.set("recipientQuery", recipientQuery);
  }
  if (recipientLimit > 0) {
    params.set("recipientLimit", `${recipientLimit}`);
  }
  if (limit > 0) {
    params.set("limit", `${limit}`);
  }

  const query = params.toString();
  const response = await fetch(
    `${API_URL}/ops/email-distribution/flights/${encodeURIComponent(flightId)}${query ? `?${query}` : ""}`,
    {
      credentials: "include",
    },
  );

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("401");
    }
    let message = `Failed to load email distribution details (${response.status})`;
    try {
      const payload = await response.json();
      if (payload?.message) {
        message = payload.message;
      }
    } catch (_error) {
      // ignore parse error
    }
    throw new Error(message);
  }

  return response.json();
};

const triggerOverride = async (payload: OverridePayload) => {
  const response = await fetch(`${API_URL}/ops/email-distribution/override`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("401");
    }
    let message = `Failed to trigger manual send (${response.status})`;
    try {
      const errorPayload = await response.json();
      if (errorPayload?.message) {
        message = errorPayload.message;
      }
    } catch (_error) {
      // ignore parse error
    }
    throw new Error(message);
  }

  return response.json();
};

export const useOpsEmailDistributionDetails = (
  query: FlightDetailsQuery | null,
) => {
  const navigate = useNavigate();
  return useQuery(
    [
      "ops-email-distribution-flight-details",
      query?.flightId || null,
      query?.operatorId || null,
      query?.gufi || null,
      query?.ppsId || null,
      query?.recipientQuery || "",
      query?.recipientLimit || 12,
      query?.limit || 500,
    ],
    () => fetchOpsEmailDistributionDetails(query as FlightDetailsQuery),
    {
      enabled: Boolean(query?.flightId),
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (error: Error) => {
        if (error.message === "401") {
          clearStoredAuth();
          navigate("/");
        }
      },
    },
  );
};

export const useTriggerOpsEmailDistributionOverride = (flightId: string) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation(triggerOverride, {
    onSuccess: () => {
      queryClient.invalidateQueries([
        "ops-email-distribution-flight-details",
        flightId,
      ]);
      queryClient.invalidateQueries(["ops"]);
    },
    onError: (error: Error) => {
      if (error.message === "401") {
        clearStoredAuth();
        navigate("/");
      }
    },
  });
};
